import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query'
import { differenceInSeconds } from 'date-fns'
import { isNumber } from 'lodash'

import { getQueryRefetchOptions } from 'consts/query/query'
import { QueryKey } from 'consts/query/queryKey'
import { Security } from 'modules/security/security'
import { StorageKey } from 'modules/storage/const'
import { ClientStorage } from 'modules/storage/storage'
import {
  AlertingActionGetResponse,
  AlertingEntityType,
  AlertingMetricsGetResponse,
  AlertingPolicyCreateRequest,
  CompleteAlertingIncidentActionParams,
  IncidentTimelineSearchRequest,
  IncidentTimelineSearchResponse,
  SkipAlertingIncidentActionParams,
  SpecialisationDataWithoutSystem,
  UUID,
} from 'types'

import {
  acknowledgeAlertingIncident,
  closeAlertingIncident,
  completeAlertingIncidentAction,
  createAlertingPolicy,
  deleteAlertingPolicy,
  getAlertingActions,
  getAlertingIncidentsTimelines,
  getAlertingMetrics,
  getAlertingPolicies,
  getAlertingSlackUserGroups,
  refreshAlertingSlackUserGroups,
  skipAlertingIncidentAction,
} from './requests'
import { GetAlertingPoliciesParams } from './types'

const lastRefreshSlackUserGroupsStorage = new ClientStorage<number>(
  StorageKey.LastRefreshSlackUserGroups,
)

export const useAlertingSlackUserGroupsQuery = () => {
  return useQuery({
    queryKey: [QueryKey.GetAlertingSlackUserGroups],
    queryFn: () => getAlertingSlackUserGroups(),
  })
}
export const useAlertingPoliciesQuery = <
  T extends { specialisation?: SpecialisationDataWithoutSystem },
>(
  params?: T,
) => {
  return useQuery({
    queryKey: [QueryKey.GetAlertingPolicies, params],
    queryFn: () =>
      getAlertingPolicies({
        // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
        entityId: params?.specialisation?.id!,
        entityType: AlertingEntityType.Specialisation,
      }),
    enabled: Security.canViewPolicies && Boolean(params?.specialisation),
  })
}
export const useAlertingMetricsQuery = (
  params: GetAlertingPoliciesParams,
  options: Partial<UseQueryOptions<AlertingMetricsGetResponse>> = {},
) => {
  return useQuery({
    queryKey: [QueryKey.GetAlertingMetrics, params],
    queryFn: () => getAlertingMetrics(params),
    ...options,
  })
}
export const useAlertingIncidentsTimelinesQuery = (
  body: IncidentTimelineSearchRequest,
  options?: Partial<UseQueryOptions<IncidentTimelineSearchResponse>>,
) => {
  return useQuery({
    queryKey: [QueryKey.GetAlertingIncidentsTimelines, body],
    queryFn: () => getAlertingIncidentsTimelines(body),
    ...options,
    ...getQueryRefetchOptions<IncidentTimelineSearchResponse>(30_000),
  })
}
export const useAlertingActionsQuery = (
  options?: Partial<UseQueryOptions<AlertingActionGetResponse>>,
) => {
  return useQuery({
    queryKey: [QueryKey.GetAlertingActions],
    queryFn: () => getAlertingActions(),
    ...options,
    ...getQueryRefetchOptions<AlertingActionGetResponse>(30_000),
  })
}
export const useAlertingIncidentsTimelinesMutation = (
  body: IncidentTimelineSearchRequest,
) => {
  return useMutation({
    mutationFn: () => getAlertingIncidentsTimelines(body),
  })
}
export const useCreateAlertingPolicy = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (body: AlertingPolicyCreateRequest) => createAlertingPolicy(body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.GetQueuesHealthDetails],
      })
      queryClient.invalidateQueries({
        queryKey: [QueryKey.GetAlertingPolicies],
      })
      queryClient.invalidateQueries({
        queryKey: [QueryKey.GetAlertingIncidentsTimelines],
      })
    },
  })
}
export const useDeleteAlertingPolicy = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (alertingPolicyId: UUID) => deleteAlertingPolicy(alertingPolicyId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.GetQueuesHealthDetails],
      })
      queryClient.invalidateQueries({
        queryKey: [QueryKey.GetAlertingPolicies],
      })
      queryClient.invalidateQueries({
        queryKey: [QueryKey.GetAlertingIncidentsTimelines],
      })
    },
  })
}
export const useRefreshAlertingSlackUserGroups = () => {
  const lastSaveTimestamp = lastRefreshSlackUserGroupsStorage.getValue()
  const isFresh =
    isNumber(lastSaveTimestamp) && differenceInSeconds(Date.now(), lastSaveTimestamp) < 60

  return useQuery({
    queryKey: [QueryKey.RefreshAlertingSlackUserGroups],
    queryFn: async () => {
      const result = await refreshAlertingSlackUserGroups()
      lastRefreshSlackUserGroupsStorage.setValue(Date.now())

      return result
    },
    enabled: !isFresh && Security.canRefreshSlackUserGroups,
    staleTime: 0,
    gcTime: 0,
  })
}
export const useCloseAlertingIncident = (
  options?: Partial<UseMutationOptions<string[], unknown, UUID>>,
) => {
  return useMutation({
    mutationFn: (incidentId: UUID) => closeAlertingIncident(incidentId),
    ...options,
  })
}
export const useAcknowledgeAlertingIncident = (
  options?: Partial<UseMutationOptions<string[], unknown, UUID>>,
) => {
  return useMutation({
    mutationFn: (incidentId: UUID) => acknowledgeAlertingIncident(incidentId),
    ...options,
  })
}
export const useCompleteAlertingIncidentAction = (
  options?: Partial<
    UseMutationOptions<unknown, unknown, CompleteAlertingIncidentActionParams>
  >,
) => {
  return useMutation({
    mutationFn: (params: CompleteAlertingIncidentActionParams) =>
      completeAlertingIncidentAction(params),
    ...options,
  })
}
export const useSkipAlertingIncidentAction = (
  options?: Partial<
    UseMutationOptions<unknown, unknown, SkipAlertingIncidentActionParams>
  >,
) => {
  return useMutation({
    mutationFn: (params: SkipAlertingIncidentActionParams) =>
      skipAlertingIncidentAction(params),
    ...options,
  })
}
